"use client";

import React, { useEffect, useRef, useState } from "react";
import { Section, Typography } from "@/components/ui";

export interface HowItWorksStep {
  number: string;
  text: string;
}

interface HowItWorksSectionProps {
  /** Section title */
  title?: string;
  /** Section description */
  description?: string;
  /** Steps to display */
  steps?: HowItWorksStep[];
  /** Optional section background */
  className?: string;
}

const defaultSteps: HowItWorksStep[] = [
  { number: "01", text: "Create message" },
  { number: "02", text: "Select audience" },
  { number: "03", text: "Choose channels" },
  { number: "04", text: "Go live" },
  { number: "05", text: "Track reach" },
];

export function HowItWorksSection({
  title = "How It Works",
  description = "How to use Pickcel to reach your employees on screens, desktops, and phones.",
  steps = defaultSteps,
  className = "bg-white",
}: HowItWorksSectionProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [visibleSteps, setVisibleSteps] = useState<boolean[]>(() => steps.map(() => false));
  const [hasAnimated, setHasAnimated] = useState(false);

  // Reset when steps change
  useEffect(() => {
    setVisibleSteps(steps.map(() => false));
    setHasAnimated(false);
  }, [steps]);

  // Trigger animation when the section scrolls into view
  useEffect(() => {
    if (!containerRef.current || typeof window === "undefined" || hasAnimated) return;

    const sectionEl = containerRef.current;

    const observer = new IntersectionObserver(
      entries => {
        const entry = entries[0];
        if (!entry?.isIntersecting) return;

        setHasAnimated(true);
        observer.disconnect();

        // Desktop: show all at once
        if (window.innerWidth >= 768) {
          setVisibleSteps(steps.map(() => true));
          return;
        }

        // Mobile: sequential fade-in (slower and steadier)
        steps.forEach((_, index) => {
          setTimeout(() => {
            setVisibleSteps(prev => {
              if (prev[index]) return prev;
              const next = [...prev];
              next[index] = true;
              return next;
            });
          }, index * 500);
        });
      },
      { threshold: 0.25 }
    );

    observer.observe(sectionEl);
    return () => observer.disconnect();
  }, [steps, hasAnimated]);

  return (
    <Section variant="default" className={className}>
      <div className="container bg-gray-50 border w-[296px] py-10 sm:w-full border-[#EDEDED] rounded-2xl mx-auto max-w-7xl px-4 sm:px-6">
        <Typography
          variant="h2"
          as="h2"
          className="text-center  font-bold mb-4 text-text-primary"
        >
          {title}
        </Typography>
        <Typography
          variant="body"
          as="p"
          className="text-center text-text-secondary"
        >
          {description}
        </Typography>

        {/* Mobile: stacked with scroll-triggered fade-in, Desktop: horizontal row */}
        <div
          ref={containerRef}
          className="flex flex-col lg:flex-row items-center justify-center gap-3 mt-10 md:gap-2"
        >
          {steps.map((step, idx) => (
            <React.Fragment key={idx}>
              {/* Step Card - Horizontal layout */}
              <div
                data-step-index={idx}
                className={`bg-white rounded-xl border border-gray-100 py-4 px-4 md:py-4 md:px-5 flex items-center gap-3 w-full max-w-[280px] md:w-auto md:max-w-none transition-all duration-700 ease-out ${
                  visibleSteps[idx] ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
                } md:opacity-100 md:translate-y-0`}
              >
                {/* Circle with number */}
                <div className="w-10 h-10 md:w-11 md:h-11 rounded-full bg-white border border-[#666666] flex items-center justify-center flex-shrink-0">
                  <span className="text-[#5B6CFF] font-semibold text-sm md:text-base">
                    {step.number}
                  </span>
                </div>
                {/* Text label */}
                <Typography
                  variant="body"
                  as="p"
                  className="text-text-primary font-semibold text-sm md:text-base leading-tight"
                >
                  {step.text}
                </Typography>
              </div>
              {/* Arrow connector - only visible on desktop */}
              {idx < steps.length - 1 && (
                <span
                  className="hidden md:flex text-gray-300 text-lg px-0.5"
                  aria-hidden="true"
                >
                  →
                </span>
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
    </Section>
  );
}
